// Suggest Story Times (StoryTimeSuggestionsSheet.swift): times for untimed scenes from their openings, each with the
// sentence it came from (the words it came from in bold), to accept in bulk: untick any, change a date, then Set Story
// Times records a "Before setting story times" checkpoint and sets the rest. With no story times yet, "The story begins
// on" gives transitions somewhere to count from.
import { useMemo, useState } from 'react';
import type { Project } from '@writing-suite/core';
import { settingHour, deviceZone } from '@writing-suite/core/story';
import { announce } from '../../shell/context.ts';
import { useWorkspace } from '../../shell/workspace.ts';
import type { LiveProject } from '../../store/project.ts';
import { applyStoryTimes, hasStoryTimes, storyTimeSuggestions } from '../../store/story.ts';
import { fromLocalInput, plural, StorySheet, toLocalInput } from './common.tsx';

export function StoryTimeSheet({ project, live, onClose }: { project: Project; live: LiveProject | null; onClose: () => void }) {
  const ws = useWorkspace();
  const [useStart, setUseStart] = useState(false);
  const [start, setStart] = useState(() => settingHour(9, 0, 0, Date.now(), deviceZone()));
  const [dates, setDates] = useState<Record<string, number>>({});
  const [result, setResult] = useState<string | null>(null);
  const timed = hasStoryTimes(project);
  // Suggested for the project as it was when the sheet opened (or the start changed): accepting rebuilds the project.
  const proposals = useMemo(() => storyTimeSuggestions(project, useStart ? start : null), [useStart, start]); // eslint-disable-line react-hooks/exhaustive-deps
  const [unticked, setUnticked] = useState<Set<string>>(new Set());
  const chosen = proposals.filter((p) => !unticked.has(p.sceneID));
  const set = () => {
    if (!live) return;
    const times = new Map(chosen.map((p) => [p.sceneID, new Date(dates[p.sceneID] ?? p.date)]));
    const n = applyStoryTimes(live, times);
    if (n > 0) {
      announce(`${plural(n, 'story time')} set. A checkpoint was recorded first.`);
      onClose();
    } else setResult('Nothing was set.');
  };
  return (
    <StorySheet title="Suggest Story Times" onClose={onClose} className="story-times-sheet">
      <div className="story-times-start">
        <label className="field-row">
          <input type="checkbox" checked={useStart} onChange={(e) => setUseStart(e.target.checked)} /> The story begins on
        </label>
        {useStart ? (
          <input
            type="datetime-local"
            aria-label="Start"
            value={toLocalInput(start)}
            onChange={(e) => {
              const v = fromLocalInput(e.target.value);
              if (v !== null) setStart(v);
            }}
          />
        ) : null}
        <p className="confirm-message small">
          {timed
            ? 'Transitions like “the next morning” count on from the nearest scene before them with a story time.'
            : 'No scene has a story time yet. Give a start date so transitions like “the next morning” can be counted from it.'}
        </p>
      </div>
      {proposals.length ? (
        <div className="story-times-head">
          <p className="eyebrow" data-proposals={proposals.length}>
            {plural(proposals.length, 'suggestion')}
          </p>
          <button type="button" className="link-button" onClick={() => setUnticked(chosen.length === proposals.length ? new Set(proposals.map((p) => p.sceneID)) : new Set())}>
            {chosen.length === proposals.length ? 'Select None' : 'Select All'}
          </button>
        </div>
      ) : (
        <p className="empty-note">
          {useStart || timed ? 'No dates or transitions found in the openings of scenes without a story time.' : 'No dates found. Give a start date to use transitions such as “the next day”.'}
        </p>
      )}
      <ul className="story-times-list">
        {proposals.map((p) => {
          const on = !unticked.has(p.sceneID);
          const at = p.context.indexOf(p.evidence);
          return (
            <li key={p.sceneID} className="story-time-row" data-scene-id={p.sceneID}>
              <input
                type="checkbox"
                checked={on}
                aria-label={p.title}
                onChange={(e) => {
                  const next = new Set(unticked);
                  if (e.target.checked) next.delete(p.sceneID);
                  else next.add(p.sceneID);
                  setUnticked(next);
                }}
              />
              <div className="story-time-body">
                <div className="story-time-top">
                  <span className="story-time-title">{p.title}</span>
                  <input
                    type="datetime-local"
                    aria-label={`Story time for ${p.title}`}
                    value={toLocalInput(dates[p.sceneID] ?? p.date)}
                    onChange={(e) => {
                      const v = fromLocalInput(e.target.value);
                      if (v !== null) setDates({ ...dates, [p.sceneID]: v });
                    }}
                  />
                </div>
                <p className="story-time-context">
                  {at >= 0 ? (
                    <>
                      {p.context.slice(0, at)}
                      <strong>{p.evidence}</strong>
                      {p.context.slice(at + p.evidence.length)}
                    </>
                  ) : (
                    p.context
                  )}
                </p>
                <p className="story-time-basis">
                  {p.basis === 'explicit' ? 'Date in the text' : 'Counted from the scene before'}{' '}
                  <button
                    type="button"
                    className="link-button"
                    onClick={() => {
                      onClose();
                      ws?.reveal({ fragmentId: p.sceneID });
                    }}
                  >
                    Show
                  </button>
                </p>
              </div>
            </li>
          );
        })}
      </ul>
      {result ? <p role="status">{result}</p> : null}
      <div className="actions">
        <button type="button" className="button secondary" onClick={onClose}>
          Close
        </button>
        <button type="button" className="button primary" disabled={!chosen.length || !live} onClick={set}>
          {chosen.length === 1 ? 'Set 1 Story Time' : `Set ${chosen.length} Story Times`}
        </button>
      </div>
    </StorySheet>
  );
}
